import React, { useState } from 'react';
import { Search, Download, Eye, AlertTriangle, Flame, Droplet, Wind, FileText, Filter, FlaskConical } from 'lucide-react';

const ChemicalInformation = ({ searchQuery = '' }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const chemicals = [
    {
      id: 1,
      name: 'Benzene',
      casNumber: '71-43-2',
      formula: 'C6H6',
      hazardClass: 'Flammable Liquid',
      flammabilityLimit: 'LEL: 1.2% UEL: 7.8%',
      reactivity: 'Stable under normal conditions',
      healthHazard: 'Carcinogen - Category 1A',
      autoIgnitionTemp: '498°C',
      flashPoint: '-11°C',
      msdaAvailable: true,
      lastUpdated: '2025-11-15',
      storageTemp: '15-25°C'
    },
    {
      id: 2,
      name: 'Methanol',
      casNumber: '67-56-1',
      formula: 'CH3OH',
      hazardClass: 'Flammable Liquid, Toxic',
      flammabilityLimit: 'LEL: 6.0% UEL: 36.5%',
      reactivity: 'Reacts with strong oxidizers',
      healthHazard: 'Acute Toxicity - Category 3',
      autoIgnitionTemp: '464°C',
      flashPoint: '11°C',
      msdaAvailable: true,
      lastUpdated: '2025-10-20',
      storageTemp: '15-25°C'
    },
    {
      id: 3,
      name: 'Hydrogen Sulfide',
      casNumber: '7783-06-4',
      formula: 'H2S',
      hazardClass: 'Toxic Gas, Flammable',
      flammabilityLimit: 'LEL: 4.3% UEL: 45.5%',
      reactivity: 'Highly reactive with oxidizers',
      healthHazard: 'Fatal if inhaled - Category 2',
      autoIgnitionTemp: '260°C',
      flashPoint: 'N/A (Gas)',
      msdaAvailable: true,
      lastUpdated: '2025-12-01',
      storageTemp: 'Pressurized storage'
    },
    {
      id: 4,
      name: 'Sulfuric Acid',
      casNumber: '7664-93-9',
      formula: 'H2SO4',
      hazardClass: 'Corrosive, Oxidizer',
      flammabilityLimit: 'Non-flammable',
      reactivity: 'Reacts violently with water',
      healthHazard: 'Skin Corrosion - Category 1A',
      autoIgnitionTemp: 'N/A',
      flashPoint: 'N/A',
      msdaAvailable: true,
      lastUpdated: '2025-11-28',
      storageTemp: '15-25°C'
    },
    {
      id: 5,
      name: 'Ammonia',
      casNumber: '7664-41-7',
      formula: 'NH3',
      hazardClass: 'Toxic Gas, Corrosive',
      flammabilityLimit: 'LEL: 15% UEL: 28%',
      reactivity: 'Reacts with acids',
      healthHazard: 'Acute Toxicity - Category 3',
      autoIgnitionTemp: '651°C',
      flashPoint: 'N/A (Gas)',
      msdaAvailable: true,
      lastUpdated: '2025-11-10',
      storageTemp: 'Refrigerated storage'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Chemicals', count: chemicals?.length },
    { id: 'flammable', name: 'Flammable', count: 3 },
    { id: 'toxic', name: 'Toxic', count: 3 },
    { id: 'corrosive', name: 'Corrosive', count: 2 },
    { id: 'oxidizer', name: 'Oxidizers', count: 1 }
  ];

  const filteredChemicals = chemicals?.filter(chemical =>
    chemical?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    chemical?.casNumber?.includes(searchQuery) ||
    chemical?.formula?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const getHazardIcon = (hazardClass) => {
    if (hazardClass?.includes('Flammable')) return <Flame className="w-5 h-5 text-orange-500" />;
    if (hazardClass?.includes('Toxic')) return <AlertTriangle className="w-5 h-5 text-red-500" />;
    if (hazardClass?.includes('Corrosive')) return <Droplet className="w-5 h-5 text-purple-500" />;
    if (hazardClass?.includes('Oxidizer')) return <Wind className="w-5 h-5 text-blue-500" />;
    return <AlertTriangle className="w-5 h-5 text-gray-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Chemicals</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{chemicals?.length}</p>
            </div>
            <FlaskConical className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Flammable</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">3</p>
            </div>
            <Flame className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Toxic</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">3</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">MSDS Updated</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{chemicals?.length}</p>
            </div>
            <FileText className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>
      {/* Filters and View Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-wrap gap-2">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setSelectedCategory(category?.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category?.id
                    ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category?.name} ({category?.count})
              </button>
            ))}
          </div>

          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50">
              <Filter className="w-5 h-5 text-gray-600" />
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
              Add Chemical
            </button>
          </div>
        </div>
      </div>
      {/* Chemicals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredChemicals?.map((chemical) => (
          <div key={chemical?.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">{chemical?.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">CAS: {chemical?.casNumber}</p>
                  <p className="text-sm text-gray-600">Formula: {chemical?.formula}</p>
                </div>
                {getHazardIcon(chemical?.hazardClass)}
              </div>

              {/* Hazard Class Badge */}
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                  {chemical?.hazardClass}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-3 mb-4">
                <div>
                  <p className="text-xs text-gray-500">Flammability Limits</p>
                  <p className="text-sm font-medium text-gray-900">{chemical?.flammabilityLimit}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Flash Point</p>
                  <p className="text-sm font-medium text-gray-900">{chemical?.flashPoint}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Auto-Ignition Temperature</p>
                  <p className="text-sm font-medium text-gray-900">{chemical?.autoIgnitionTemp}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Health Hazard</p>
                  <p className="text-sm font-medium text-red-600">{chemical?.healthHazard}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Reactivity</p>
                  <p className="text-sm font-medium text-gray-900">{chemical?.reactivity}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <button className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium">
                  <Eye className="w-4 h-4" />
                  <span>View MSDS</span>
                </button>
                <button className="flex items-center space-x-1 text-gray-600 hover:text-gray-700 text-sm font-medium">
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </button>
              </div>

              {/* Last Updated */}
              <p className="text-xs text-gray-500 mt-3">Last updated: {chemical?.lastUpdated}</p>
            </div>
          </div>
        ))}
      </div>
      {/* No Results */}
      {filteredChemicals?.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No chemicals found</h3>
          <p className="text-gray-600">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default ChemicalInformation;